<!--
  - Copyright (c) 2021 brightlamp
  -->

<template>
    <div>
        <v-toolbar flat color="white">
            <v-toolbar-title>Messages</v-toolbar-title>
            <v-badge v-if="!adminMode"
                :value = "totalUnviewedMessages"
                :color="totalUnviewedMessages ? 'red' : 'white'"
                :content="totalUnviewedMessages">
            </v-badge>
            <v-spacer/>
            <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Search by Subject"
                single-line
                hide-details
                clearable
                @click:clear="handleClearSearch"
                v-debounce:300="handleSearch">
            </v-text-field>
            <v-spacer/>
            <CreateMessageDialog :dialog="createMessageDialog" :messageToEdit="messageToEdit" @dialog-close="messageCenterDialogClose" />
        </v-toolbar>
        <v-toolbar flat color="white">
            <v-btn v-if="adminMode" class="mx-2" @click="newMessage" color="accent">
                <span style="color: #000000;">Create Message</span>
            </v-btn>
            <v-icon large class="ml-6" @click="loadItems">
                mdi-refresh
            </v-icon>
            <v-spacer/>
        </v-toolbar>
        <v-container fluid>
            <v-row no-gutters>
                <v-col>
                    <v-data-table
                        v-model="selected"
                        :headers="headers"
                        :items="messages"
                        item-key="id"
                        :must-sort=false
                        :multi-sort=true
                        @click:row="rowSelected"
                        @current-items="currentItems"
                        :single-expand=true
                        :expanded.sync="expanded"
                        :options.sync="pagination"
                        :server-items-length="totalMessages"
                        :loading="waiting"
                        :no-data-text="adminMode ? 'No messages created' : 'No messages for you'"
                        :footer-props="{'items-per-page-options':[15, 30, 50, 100]}"
                        class="elevation-1"
                        @update:options="loadItems">
                        <template v-if="adminMode" v-slot:item.actions="{ item }">
                            <v-menu offset-y>
                                <template v-slot:activator="{ on }">
                                    <v-btn color="accent" v-on="on">
                                        <span style="color: #000000;">Actions</span>
                                    </v-btn>
                                </template>
                                <v-list>
                                    <v-list-item
                                        v-for="(actionItem, index) in actionItems"
                                        :key="index"
                                        @click="messageAction(index, item)">
                                        <v-list-item-icon><v-icon color="black" v-text="actionItem.icon"></v-icon></v-list-item-icon>
                                        <v-list-item-content><v-list-item-title v-text="actionItem.title"></v-list-item-title></v-list-item-content>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </template>
                        <template v-else v-slot:item.actions="{ item }">
                            <v-btn @click="deleteMessage(item)" color="accent">
                                <v-icon style="color: #000000;" left>mdi-delete</v-icon>
                                <span style="color: #000000;">Delete</span>
                            </v-btn>
                        </template>
                        <template v-slot:item.expanded="{ item }">
                            <v-row justify-Left>
                                <v-icon large v-if="!expanded.includes(item)">mdi-chevron-right</v-icon>
                                <v-icon large v-else>mdi-chevron-down</v-icon>
                                <v-badge v-if="!adminMode" class="mt-5"
                                    :value = !item.beenViewed
                                    color="red"
                                    dot>
                                </v-badge>
                            </v-row>
                        </template>
                        <template v-slot:expanded-item="props">
                            <td :colspan=12>
                                <v-expand-transition>
                                    <v-card class="elevation-10">
                                        <v-card-text>
                                            <div style="white-space: pre-line">
                                                <p class="text-left" style="color: #000000;">{{props.item.body}}</p>
                                            </div>
                                        </v-card-text>
                                    </v-card>
                                </v-expand-transition>
                            </td>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
    import {mapActions, mapMutations, mapState} from 'vuex';
    import CreateMessageDialog from '@/components/create-message-dialog';

    export default
    {
        name: 'message-center-dashboard',
        components:
        {
            CreateMessageDialog
        },
        data: function ()
        {
            return {
                expandOrHideRow: true,
                search: '',
                pagination: {},
                selected: [],
                expanded: [],
                headers:
                [
                    {
                        text: 'Message Body',
                        value: 'expanded',
                        width: '40px',
                        sortable: false
                    },
                    {
                        text: 'Actions',
                        value: 'actions',
                        width: '100px',
                        sortable: false
                    },
                    {
                        text: 'Subject',
                        width: '110px',
                        value: 'subject',
                        sortable: false
                    },
                    {
                        text: 'Category',
                        width: '120px',
                        value: 'categoryText',
                        sortable: false
                    },
                    {
                        text: this.adminMode ? 'Created Date ' : 'Received Date',
                        value: 'updatedAt',
                        width: '190px',
                        sortable: false
                    }
                ],
                createMessageDialog: false,
                actionItems:
                [
                    { title: 'Edit', icon: 'mdi-pencil' },
                    { title: 'Delete', icon: 'mdi-delete' }
                ],
                messageToEdit: null
            }
        },
        props:
        {
            adminMode: Boolean(false),
        },
        destroyed()
        {
            this.CLEAR_MESSAGES()
        },
        computed:
        {
            ...mapState('auth',
            {
                currentUser: state => state.currentUser
            }),
            ...mapState('messages',
            {
                messages: state => state.messages,
                totalMessages: state => state.totalMessages,
                totalUnviewedMessages: state => state.totalUnviewedMessages
            }),
            ...mapState('common',
            {
                waiting: state => state.waiting
            }),
        },
        watch:
        {
            search()
            {
                this.pagination.page = 1
            }
        },
        methods:
        {
            ...mapMutations('messages', ['CLEAR_MESSAGES', 'UPDATE_MESSAGE_VIEWED_LOCALLY']),
            ...mapActions('messages', ['GET_MESSAGES', 'SET_MESSAGE_VIEWED_NO_WAIT', 'GET_UNVIEWED_MESSAGE_COUNT', 'DELETE_MESSAGE']),
            ...mapMutations('common', ['WAITING', 'SHOW_SNACKBAR_ERROR', 'HIDE_SNACKBAR']),
            async handleSearch()
            {
                await this.loadItems()
            },
            async handleClearSearch()
            {
                this.search = '';
                await this.loadItems()
            },
            rowSelected(item)
            {
                // Do not expand or hide the row if an action in a row was selected
                if (!this.expandOrHideRow)
                {
                    this.expandOrHideRow = true;
                }
                else
                {
                    this.expanded = this.expanded.length > 0 && this.expanded[0].id === item.id ? [] : [item]
                    // If we are just displaying destination messages and the message has not been marked as viewed yet, do so now
                    if (!this.adminMode && this.expanded.length > 0 && !this.expanded[0].beenViewed)
                    {
                        this.UPDATE_MESSAGE_VIEWED_LOCALLY(this.expanded[0].source_id);
                        this.SET_MESSAGE_VIEWED_NO_WAIT(this.expanded[0].source_id).catch()
                    }
                }
            },
            currentItems(items)
            {
                if (this.pagination.page !== 1 && 0 === items.length)
                {
                    this.pagination.page -= 1
                }
            },
            async messageAction(index, item)
            {
                switch (index)
                {
                    case 0: // Edit message
                        await this.editMessage(item);
                        break
                    default: // Delete message
                        await this.deleteMessage(item)
                }
            },
            async loadItems()
            {
                try
                {
                    this.HIDE_SNACKBAR();
                    this.WAITING(true);
                    await this.GET_UNVIEWED_MESSAGE_COUNT();
                    await this.GET_MESSAGES({adminMode: this.adminMode, pagination: this.pagination, search: this.search})
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message);
                }
                this.WAITING(false)
            },
            newMessage()
            {
                this.messageToEdit = null;
                this.createMessageDialog = true
            },
            shouldCollapse()
            {
                this.expandOrHideRow = false;
                // Collapse the row if switching to a new row
                if (this.expanded.length === 1)
                {
                    this.expanded = []
                }
            },
            async messageCenterDialogClose(payload)
            {
                this.createMessageDialog = false;
                if (payload)
                {
                    await this.loadItems()
                }
            },
            editMessage(item)
            {
                this.messageToEdit = item;
                this.createMessageDialog = true
            },
            async deleteMessage(item)
            {
                try
                {
                    this.expandOrHideRow = false;
                    this.HIDE_SNACKBAR();
                    this.WAITING(true);
                    // Collapse all the rows
                    this.expanded = [];
                    await this.DELETE_MESSAGE({adminMode: this.adminMode, source_id: this.adminMode ? item.id : item.source_id});
                    await this.loadItems()
                }
                catch (error)
                {
                    this.SHOW_SNACKBAR_ERROR(error.message);
                }
                this.WAITING(false)
            }
        }
    };
</script>

<style scoped>
</style>